import React, { useState } from "react";
import { toast } from "sonner";
import { QrCode, Search, Check, UserX, User, Baby, ScanLine } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { QRScanner } from "../../components/ui/QRScanner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

export default function AdminCheckIn() {
  const { getTicket, getActivity, getSlot, setCheckIn } = useApp();
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState("");
  const [scanOpen, setScanOpen] = useState(false);

  const search = (e, forcedCode) => {
    e?.preventDefault();
    setError("");
    const lookup = (forcedCode ?? code).trim().toUpperCase();
    const t = getTicket(lookup);
    if (!t) { setTicket(null); setError("Tiket tidak ditemukan. Cek kembali kodenya."); return; }
    setTicket(t);
  };

  const handleScan = (decoded) => {
    const clean = (decoded || "").trim().toUpperCase();
    setCode(clean);
    setScanOpen(false);
    toast.success(`QR terbaca: ${clean}`);
    search(null, clean);
  };

  const bookings = ticket ? ticket.bookings.filter((b) => b.status === "Confirmed") : [];

  const mark = (ref, status) => {
    setCheckIn(ticket.ticketCode, ref, status);
    setTicket((prev) => ({ ...prev, bookings: prev.bookings.map((b) => (b.ref === ref ? { ...b, checkIn: status } : b)) }));
    toast.success(status === "Checked In" ? "Peserta checked in! 🎉" : "Ditandai No Show.");
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-sea/15 text-sea flex items-center justify-center">
          <QrCode className="w-8 h-8" />
        </div>
        <h1 className="mt-3 font-display text-2xl sm:text-3xl text-slate-800">Check-In</h1>
        <p className="text-slate-500 font-semibold">Cari tiket lalu check in peserta di lokasi.</p>
      </div>

      <form onSubmit={search} className="mt-6 flex gap-2">
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="BLTR26-AB12CD"
            data-testid="checkin-code"
            className="w-full pl-10 pr-3 py-3.5 rounded-xl border-2 border-slate-300 focus:border-sea outline-none font-display tracking-wider text-lg text-slate-700 uppercase"
          />
        </div>
        <button type="submit" className="px-6 rounded-xl bg-sea text-white font-display text-lg" data-testid="checkin-search">Cari</button>
      </form>

      <button
        onClick={() => setScanOpen(true)}
        data-testid="checkin-scan-open"
        className="mt-3 w-full flex items-center justify-center gap-2 bg-grass text-white font-display text-lg py-3.5 rounded-xl active:scale-95 transition-transform"
      >
        <ScanLine className="w-6 h-6" /> Scan QR dengan Kamera
      </button>

      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-800 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-sea" /> Scan Tiket QR
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm font-semibold text-slate-500 -mt-1">Arahkan kamera ke QR code pada tiket peserta.</p>
          {scanOpen && <QRScanner onScan={handleScan} />}
        </DialogContent>
      </Dialog>

      {error && <p className="mt-4 text-center bg-destructive/10 text-destructive font-bold rounded-xl py-3" data-testid="checkin-error">{error}</p>}

      {ticket && (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="checkin-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-display text-2xl text-coral">{ticket.ticketCode}</p>
              <StatusBadge status={ticket.paymentStatus} />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <span className="flex items-center gap-2 text-slate-600 font-semibold"><User className="w-4 h-4 text-sea" /> {ticket.parentName}</span>
            <span className="flex items-center gap-2 text-slate-600 font-semibold"><Baby className="w-4 h-4 text-sea" /> {ticket.childName} ({ticket.childAge}th)</span>
          </div>

          <div className="mt-6">
            <p className="font-display text-lg text-slate-800">Aktivitas Terdaftar</p>
            {bookings.length ? (
              <div className="mt-3 space-y-3">
                {bookings.map((b) => {
                  const a = getActivity(b.activityId);
                  const s = getSlot(b.activityId, b.slotId);
                  return (
                    <div key={b.ref} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <img src={a?.icon} alt="" className="w-10 h-10 object-contain" />
                        <div className="flex-1">
                          <p className="font-display text-slate-800">{a?.name}</p>
                          <p className="text-xs font-bold text-slate-500">{s ? `${s.start}–${s.end}` : ""} · Ref {b.ref}</p>
                        </div>
                        <StatusBadge status={b.checkIn} />
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          disabled={b.checkIn === "Checked In"}
                          onClick={() => mark(b.ref, "Checked In")}
                          data-testid={`big-checkin-${b.ref}`}
                          className="flex-1 flex items-center justify-center gap-2 bg-grass text-white font-display text-lg py-3.5 rounded-xl disabled:opacity-40 active:scale-95 transition-transform"
                        >
                          <Check className="w-6 h-6" /> CHECK IN
                        </button>
                        <button
                          onClick={() => mark(b.ref, "No Show")}
                          data-testid={`noshow-${b.ref}`}
                          className="px-4 rounded-xl border-2 border-slate-200 text-slate-500 font-semibold"
                        >
                          <UserX className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 text-slate-400 font-semibold">Belum ada aktivitas terbooking untuk tiket ini.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
